import { ActivatedRouteSnapshot, Params, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { catchError, map, Observable, of } from 'rxjs';
import { AuthService } from '../../services/auth-service/auth.service';
import { inject, Injectable } from '@angular/core';
import { ToastService } from '@tc-angular/design-system';
import { OBSERVABILITY_FACADE } from '../../services/facades';
import {
  GuardNavigationErrorSource,
  GuardNavigationFailureTestId,
  reportGuardNavigationFailure,
} from 'src/app/shared/services/guard-navigation-failure/guard-navigation-failure';

@Injectable({ providedIn: 'root' })
export class AuthGuard {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly observabilityFacade = inject(OBSERVABILITY_FACADE);
  private readonly toastService = inject(ToastService);

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): boolean | UrlTree | Observable<boolean | UrlTree> {
    const initialString = 'Initial';
    const myTcUser: any = this.authService.getUserCookie();
    const redirectUrl: string = state.url;
    const paymentRef: string = this.establishPaymentRef(route, state, initialString);

    if (state.url.startsWith('/Payments/Link')) {
      return this.handlePaymentsLinkUrl(route, myTcUser, state, paymentRef, initialString);
    }
    if (myTcUser) {
      return true;
    }

    this.returnToLoginPage(route, redirectUrl);
    return false;
  }

  private handlePaymentsLinkUrl(
    route: ActivatedRouteSnapshot,
    myTcUser: any,
    state: RouterStateSnapshot,
    paymentRef: string,
    initialString: string,
  ): UrlTree | Observable<UrlTree> {
    if (myTcUser) {
      const queryParams = {
        amount: this.getParameterByName('amount', state.url),
      };

      if (paymentRef !== initialString) {
        return this.router.createUrlTree(['/Payments', paymentRef, 'BillingDetails'], {
          queryParams: queryParams,
        });
      }

      const currency: string = this.getParameterByName('currency', state.url);
      if (currency) {
        queryParams['currency'] = currency;
      }

      const quoteId: string = this.getParameterByName('quoteId', state.url);

      if (quoteId) {
        queryParams['quoteId'] = quoteId;
      }

      return this.router.createUrlTree(['/Payments', initialString, 'BillingDetails'], {
        queryParams: queryParams,
      });
    }

    if (route.queryParams['emailAddress'] && route.queryParams['customerId']) {
      return this.authService.getAccountStatus(route.queryParams['emailAddress']).pipe(
        map((active) => {
          if (active) {
            return this.createLoginUrlTree(route, state.url);
          }

          return this.router.createUrlTree(['/Account/Register'], {
            queryParams: {
              returnUrl: state.url,
              customerEmail: route.queryParams['emailAddress'],
              customerId: route.queryParams['customerId'],
            },
          });
        }),
        catchError((error) => {
          reportGuardNavigationFailure(
            error,
            GuardNavigationErrorSource.authGuardPaymentsLinkAccountStatus,
            this.observabilityFacade,
            this.toastService,
            GuardNavigationFailureTestId.authPaymentsLink,
          );

          return of(this.createLoginUrlTree(route, state.url));
        }),
      );
    }

    return this.createLoginUrlTree(route, state.url);
  }

  private createLoginUrlTree(route: ActivatedRouteSnapshot, pageUrl: string): UrlTree {
    const queryParams: Params = { returnUrl: pageUrl };

    if (route.queryParams?.email) {
      queryParams['email'] = route.queryParams.email;
    }

    return this.router.createUrlTree(['/Login'], { queryParams });
  }

  private establishPaymentRef(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
    initialString: string,
  ): string {
    let paymentRef: string = this.getRef(route, state, 'booking');
    if (!paymentRef) {
      paymentRef = this.getRef(route, state, 'quote');
    }

    if (!paymentRef) {
      paymentRef = initialString;
    }

    return paymentRef;
  }

  private getRef(route: ActivatedRouteSnapshot, state: RouterStateSnapshot, refType: 'quote' | 'booking'): string {
    let ref = '';
    let param: string;
    if (refType === 'quote') {
      param = 'quoteRef';
    } else {
      const hasBookingId: boolean = window.location.href.indexOf('bookingId') > -1;
      param = hasBookingId ? 'bookingId' : 'bookingReference';
    }

    ref = route.queryParams[param] ?? this.getParameterByName(param, state.url);
    return ref;
  }

  private getParameterByName(name: string, url: string): string {
    if (!url) {
      url = window.location.href;
    }
    url = decodeURIComponent(url);
    /* eslint-disable */
    name = name.replace(/[\[\]]/g, '\\$&');
    const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
      results = regex.exec(url);
    if (!results) {
      return null;
    }
    if (!results[2]) {
      return '';
    }
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
  }

  private returnToLoginPage(route: ActivatedRouteSnapshot, pageUrl: string) {
    const queryParams: Params = { returnUrl: pageUrl };
    if (route.queryParams?.email) {
      queryParams['email'] = route.queryParams.email;
    }
    this.router.navigate(['/Login'], { queryParams: queryParams, replaceUrl: true });
  }
}
